/**
 * StatusPanel — character overview: class, level, XP, DPS, skill points, lifetime stats.
 *
 * @module ui/panels/StatusPanel
 */

import { BasePanel } from './BasePanel.js';
import { heroDerivedStats } from '../../core/combat.js';
import { lockRemainingMs, formatLockTime } from '../../core/inventory.js';
import { xpToNext } from '../../core/progression.js';
import { bus } from '../../core/events.js';

export class StatusPanel extends BasePanel {
  constructor(opts) {
    super({
      name: 'status',
      title: 'STATUS',
      tabs: [
        { id: 'overview', label: 'Visão Geral' },
        { id: 'skills', label: 'Skills' },
      ],
      state: opts.state,
      data: opts.data,
      hooks: opts.hooks,
    });

    // Which active hero this panel is showing (0 or 1).
    this._heroIndex = 0;

    // Track DPS. NOT in this._subs: BasePanel clears _subs on every close, and
    // this tracker must keep sampling while the panel is closed (otherwise the
    // DPS reading freezes forever after the first open/close).
    this._recentDamage = [];
    this._dpsOff = bus.on('tick', (tick) => {
      if (!tick || !tick.events) return;
      for (const evt of tick.events) {
        if (evt.type === 'hero_attack') {
          this._recentDamage.push(evt.damage ?? 0);
        }
      }
      // Keep last 30 ticks
      if (this._recentDamage.length > 30) {
        this._recentDamage = this._recentDamage.slice(-30);
      }
    });
  }

  onDestroy() {
    this._dpsOff?.();
  }

  render() {
    if (this._activeTab === 'skills') {
      this._renderSkills();
    } else {
      this._renderOverview();
    }
    this._renderHeroTabs();
  }

  /** The active party hero this panel is currently showing. */
  _activeHero() {
    const party = this.state.party ?? [];
    if (this._heroIndex >= party.length) this._heroIndex = 0;
    return party[this._heroIndex] ?? party[0];
  }

  /**
   * Open this panel focused on a specific party hero + tab. Called when the
   * player clicks a hero in the game view to manage its skills.
   * @param {number} index party index
   * @param {string} [tab] 'skills' | 'overview'
   */
  focusHero(index, tab = 'skills') {
    const party = this.state.party ?? [];
    this._heroIndex = Math.max(0, Math.min(index, party.length - 1));
    this.switchTab(tab);
  }

  /** A per-hero selector at the top of the body, shown only with a duo. */
  _renderHeroTabs() {
    const party = this.state.party ?? [];
    if (party.length <= 1) return;
    const tabs = document.createElement('div');
    tabs.className = 'pd-equip-herotabs';
    tabs.style.marginBottom = '10px';
    tabs.innerHTML = party.map((h, i) => {
      const label = this.data.classes[h.classId]?.label ?? h.classId;
      const lockMs = lockRemainingMs(h);
      const lock = lockMs > 0 ? ` 🔒 ${formatLockTime(lockMs)}` : '';
      return `<button class="pd-equip-herotab${i === this._heroIndex ? ' active' : ''}" data-hero="${i}">${label}${lock}</button>`;
    }).join('');
    this.body.prepend(tabs);
    tabs.querySelectorAll('.pd-equip-herotab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._heroIndex = Number(btn.dataset.hero) || 0;
        this.render();
      });
    });
  }

  _renderOverview() {
    const hero = this._activeHero();
    const cls = this.data.classes[hero.classId];
    if (!cls) return;

    const hs = heroDerivedStats(hero, this.data.classes, hero.equipped, this.data.skills);

    // XP progress — same curve function the level-up logic uses (progression.js),
    // so the bar never disagrees with when the level actually happens.
    const xpNext = xpToNext(hero.level, this.data.balance?.progression ?? { xpBase: 20, xpExp: 1.5 });
    const xpPct = Math.min(100, Math.round((hero.xp / xpNext) * 100));

    // DPS
    const totalDmg = this._recentDamage.reduce((s, d) => s + d, 0);
    const dps = this._recentDamage.length ? (totalDmg / this._recentDamage.length).toFixed(1) : '0.0';

    // Death-lockout countdown banner (hours + minutes).
    const lockMs = lockRemainingMs(hero);
    const lockBanner = lockMs > 0
      ? `<div style="background:rgba(255,90,90,0.12);border:1px solid #b84a4a;border-radius:6px;padding:6px 10px;margin-bottom:10px;font-size:11px;color:#ff9a9a;text-align:center">
           🔒 Herói caído — disponível em <b>${formatLockTime(lockMs)}</b> · use um Kit de Reviver para liberar agora
         </div>`
      : '';

    this.body.innerHTML = `${lockBanner}`.concat(`
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px">
        <img src="assets/sprites/portraits/${hero.classId}.png"
             style="height:72px;image-rendering:pixelated" alt="${cls.label}" />
        <div>
          <div style="font-size:1.1rem;font-weight:800;color:var(--pd-chrome-text)">${cls.label}</div>
          <div style="color:var(--pd-muted);font-size:11px">
            ${ROLE_LABEL[cls.role] ?? cls.role} · Nv.${hero.level}
          </div>
          <div style="color:var(--pd-muted);font-size:10px">Primary: ${cls.primary}</div>
        </div>
      </div>

      <!-- XP Bar -->
      <div class="pd-section-title">Experiência</div>
      <div style="background:var(--pd-bg);border:1px solid var(--pd-border);border-radius:6px;height:18px;overflow:hidden;position:relative;margin-bottom:2px">
        <div style="background:linear-gradient(90deg,#7a4a1d,var(--pd-gold));height:100%;width:${xpPct}%;transition:width 0.3s ease;border-radius:5px"></div>
        <span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:10px;color:#fff;text-shadow:0 1px 0 #000">${hero.xp} / ${xpNext} XP</span>
      </div>
      <div style="font-size:10px;color:var(--pd-muted);margin-bottom:10px">${xpPct}% para Nv.${hero.level + 1}</div>

      <!-- DPS -->
      <div class="pd-section-title">DPS Médio (30 ticks)</div>
      <div style="font-size:1.6rem;font-weight:800;color:var(--pd-gold);font-family:var(--pd-font-mono);margin-bottom:10px">${dps} <span style="font-size:11px;color:var(--pd-muted)">/ tick</span></div>

      <!-- Stats -->
      <div class="pd-section-title">Atributos</div>
      <div class="pd-stat-grid" style="grid-template-columns:1fr auto;gap:4px 10px">
        <span>❤ Vida Máx</span><b>${hs.maxHp}</b>
        <span>⚔ Ataque</span><b>${Math.round(hs.atk)}</b>
        <span>🛡 Defesa</span><b>${Math.round(hs.def)}</b>
        <span>✦ Magia</span><b>${Math.round(hs.mag)}</b>
        <span>🛡 Res. Mágica</span><b>${Math.round(hs.res)}</b>
        <span>⚡ Vel. Ataque</span><b>${hs.atkSpd.toFixed(2)}</b>
        <span>✸ Crít</span><b>${Math.round(hs.crit * 100)}%</b>
        <span>💥 Dano Crít</span><b>${Math.round(hs.critDmg * 100)}%</b>
        <span>➹ Esquiva</span><b>${Math.round(hs.dodge * 100)}%</b>
      </div>

      <!-- Lifetime stats -->
      <div class="pd-section-title">Jornada</div>
      <div class="pd-stat-grid" style="grid-template-columns:1fr auto;gap:4px 10px">
        <span>☠ Monstros</span><b>${this.state.stats.kills}</b>
        <span>⛁ Ouro Total</span><b>${this.state.stats.totalGold}</b>
        <span>⏲ Ticks</span><b>${this.state.stats.ticks}</b>
        <span>⚰ Mortes</span><b>${this.state.stats.deaths ?? 0}</b>
      </div>
    `);
  }

  _renderSkills() {
    const hero = this._activeHero();
    const classSkills = (this.data.skills ?? {})[hero.classId] ?? [];
    const learned = new Set(hero.skills ?? []);
    const points = hero.skillPoints ?? 0;

    // Group skills by tier
    const byTier = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const sk of classSkills) byTier[sk.tier]?.push(sk);

    // Each skill costs `cost` points (defaults to its tier) — higher tiers cost
    // several levels' worth of points, so the tree is earned, not handed out.
    const skillCost = (sk) => sk.cost ?? sk.tier ?? 1;
    const canLearn = (sk) => {
      if (learned.has(sk.id)) return false;
      if (points < skillCost(sk)) return false;
      return sk.requires.every(r => learned.has(r));
    };

    const skillCard = (sk) => {
      const isLearned = learned.has(sk.id);
      const available = canLearn(sk);
      const border = isLearned
        ? 'var(--pd-gold)'
        : available ? '#6aad6a' : 'var(--pd-border)';
      const bg = isLearned ? 'rgba(232,180,84,0.12)' : 'var(--pd-bg)';
      const opacity = (!isLearned && !available) ? '0.5' : '1';
      const btnAttr = available ? `data-skill="${sk.id}"` : '';
      const iconHtml = `<span style="display:inline-flex;align-items:center;justify-content:center;height:32px">
          <img src="assets/sprites/skills/icon_${sk.id}.png" width="32" height="32"
               style="image-rendering:pixelated" alt=""
               onerror="this.style.display='none';this.nextElementSibling.style.display='inline'">
          <span style="display:none;font-size:1.4rem;line-height:1">${sk.icon}</span>
        </span>`;
      return `
        <div style="border:1.5px solid ${border};border-radius:8px;padding:8px 10px;background:${bg};opacity:${opacity};min-width:110px;max-width:130px;text-align:center">
          <div style="line-height:1">${iconHtml}</div>
          <div style="font-size:11px;font-weight:700;color:var(--pd-chrome-text);margin:3px 0 2px">${sk.name}</div>
          <div style="font-size:10px;color:var(--pd-muted);margin-bottom:6px">${sk.desc}</div>
          ${isLearned
            ? `<span style="font-size:9px;color:var(--pd-gold);font-weight:700">✓ APRENDIDO</span>
               ${sk.visualAttack ? (() => {
                 const act = hero.activeSkillVisual === sk.id;
                 return `<div style="margin-top:4px"><button data-use="${sk.id}" class="pd-use-btn"
                   style="font-size:9px;padding:2px 8px;cursor:pointer;border-radius:4px;font-weight:700;
                          border:1px solid ${act ? 'var(--pd-gold)' : 'var(--pd-border)'};
                          background:${act ? 'rgba(232,180,84,0.25)' : 'transparent'};
                          color:${act ? 'var(--pd-gold)' : 'var(--pd-muted)'}">
                   ${act ? '★ ataque ativo' : 'Usar ataque'}</button></div>`;
               })() : ''}`
            : available
              ? `<button ${btnAttr} class="pd-skill-btn" style="font-size:10px;padding:3px 10px;cursor:pointer;background:linear-gradient(90deg,#7a4a1d,var(--pd-gold));border:none;border-radius:4px;color:#1a1620;font-weight:700">Aprender · ${skillCost(sk)}p</button>`
              : `<span style="font-size:9px;color:var(--pd-muted)">${sk.requires.every(r => learned.has(r)) ? `🔒 ${skillCost(sk)} pontos` : 'Bloqueado'}</span>`
          }
        </div>`;
    };

    const tierRow = (tier, label) => `
      <div style="margin-bottom:4px;font-size:9px;color:var(--pd-muted);text-transform:uppercase;letter-spacing:1px">${label}</div>
      <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;margin-bottom:8px">
        ${byTier[tier].map(skillCard).join('')}
      </div>`;

    const connector = `<div style="text-align:center;color:var(--pd-muted);font-size:14px;margin:2px 0">│</div>`;

    this.body.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
        <span style="font-size:1rem;font-weight:800;color:var(--pd-chrome-text)">Árvore de Skills</span>
        <span class="pd-pill" style="border-color:${points > 0 ? 'var(--pd-gold)' : 'var(--pd-border)'};color:${points > 0 ? 'var(--pd-gold)' : 'var(--pd-muted)'}">
          ${points} ponto${points !== 1 ? 's' : ''}
        </span>
      </div>
      ${tierRow(1, 'Básico')}
      ${connector}
      ${tierRow(2, 'Avançado')}
      ${connector}
      ${tierRow(3, 'Mestre')}
      ${connector}
      ${tierRow(4, 'Lendário')}
      ${connector}
      ${tierRow(5, 'Supremo')}
      ${connector}
      ${tierRow(6, 'Divino')}
    `;

    // Wire up learn buttons
    this.body.querySelectorAll('[data-skill]').forEach(btn => {
      btn.addEventListener('click', () => {
        const skillId = btn.dataset.skill;
        const sk = classSkills.find(s => s.id === skillId);
        if (!sk || !canLearn(sk)) return;
        hero.skills = [...(hero.skills ?? []), skillId];
        hero.skillPoints = Math.max(0, (hero.skillPoints ?? 0) - skillCost(sk));
        this.hooks?.onChange?.();
        // Full render (not just _renderSkills): keeps the duo hero tabs alive.
        this.render();
      });
    });

    // Toggle which learned visual-attack skill is the active one.
    this.body.querySelectorAll('[data-use]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.use;
        hero.activeSkillVisual = hero.activeSkillVisual === id ? null : id;
        this.hooks?.onChange?.();
        this.render();
      });
    });
  }
}

const ROLE_LABEL = { tank: 'Tanque', dps: 'Dano', support: 'Suporte' };
